import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Inject, NgZone, OnDestroy, OnInit, PLATFORM_ID, QueryList, Signal, ViewChild, ViewChildren, afterNextRender, computed, effect, inject, signal } from '@angular/core';
import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import { SignalsStoreService } from '../shared/signals-store.service';
import { SidebarComponent } from '../shared/sidebar/sidebar.component';
import { ProductsService } from '../product/products.service';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { auditTime, bufferWhen, catchError, filter, from, map, mergeMap, of, scan, skip, startWith, Subject, switchMap, tap, timer, toArray } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MEDIUM } from "../../scss/responsive/responsive";
import { HeaderService } from '../shared/header/header.service';
import { ResolutionService } from '../shared/resolution.service';
import { EmptyMessageComponent } from '../shared/empty-message/empty-message.component';
import { SearchBarComponent } from '../shared/header/search-bar/search-bar.component';
import { MenuService } from '../shared/menu.service';
import { FilteringTagsComponent } from '../shared/shop-header/filtering-tags/filtering-tags.component';
import { ProductCardV2Component } from "../shared/product-card-v2/product-card-v2.component";
import { ProductResume, Product } from '../product/product.types';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderService } from '../shared/order.service';
import { MatChipsModule } from '@angular/material/chips';
import { capitalizeWords, formatStringForURL, getCurrentPathFromUrl } from '../shared/common/utils';
import { CanonicalService } from '../shared/canonical.service';
import { environment } from '../../environments/environment';
import { TypesenseSearchService } from '../shared/typesense-search.service';

@Component({
  selector: 'app-shop',
  templateUrl: './shop.component.html',
  styleUrl: './shop.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    SidebarComponent,
    NgbModule,
    MatProgressBarModule,
    EmptyMessageComponent,
    SearchBarComponent,
    EmptyMessageComponent,
    FilteringTagsComponent,
    ProductCardV2Component,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
  ]
})
export class ShopComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChildren('subcategory')
  subcategories!: QueryList<ElementRef>;

  @ViewChildren('subcategorySon')
  subcategoriesSon!: QueryList<ElementRef>;

  #productsService = inject(ProductsService);
  #headerService = inject(HeaderService);
  #resolutionService = inject(ResolutionService);
  #menuService = inject(MenuService);
  #activatedRoute = inject(ActivatedRoute);
  #orderService = inject(OrderService);
  #location = inject(Location);
  #ngZone = inject(NgZone);
  #canonicalService = inject(CanonicalService);
  #router = inject(Router);
  #typesenseService = inject(TypesenseSearchService);
  #destroyRef = inject(DestroyRef);

  signalsStore = inject(SignalsStoreService);

  isShowingShopFilters = computed(() => this.#headerService.displaySearchBar());
  isFilteringByTags = computed(() => this.#checkTagsFilter());

  lastCategory: string | null = null;
  lastSubcategory: string | null = null;
  lastSubcategorySon: string | null = null;

  productsSignal: Signal<ProductResume[]> = computed(() => {
    const sortedProducts = this.#productsService.productsSignal();
    const searchMode = this.isInSearchMode();

    let lastCategory: number = -1;
    let lastSubcategory: number = -1;
    let lastSubcategorySon: number = -1;

    this.lastSubcategorySon = null;
    this.lastSubcategory = null;
    this.lastCategory = null;

    return sortedProducts.map((product: any) => {

      product.subCategorySonInView = null;
      product.categoryInView = null;
      product.subCategoryInView = null;

      // Search results are relevance-ordered, not category-grouped: the category/subcategory
      // banner logic below would emit a banner on nearly every card. Skip straight to the
      // anchor links.
      if (searchMode) {
        this.#setupProductAnchorLinks(product);
        return product as ProductResume;
      }

      let categoryId = 0,
        subCategoryId = 0,
        subCategorySonId = 0;

      if (product.category) {
        categoryId = +product.category.id;
        if (categoryId !== lastCategory) {
          product.categoryInView = this.#getCategoryBanner(categoryId);
          lastCategory = categoryId;
        }
      }

      if (product.subcategory) {

        subCategoryId = +product.subcategory.id;
        if (subCategoryId !== lastSubcategory) {
          product.subCategoryInView = this.#getCategoryBanner(categoryId, subCategoryId);
          lastSubcategory = subCategoryId;
        }
      }

      if (product.subcategorySon) {
        subCategorySonId = +product.subcategorySon.id;
        if (subCategorySonId !== lastSubcategorySon) {
          product.subCategorySonInView = this.#getCategoryBanner(categoryId, subCategoryId, subCategorySonId);
          lastSubcategorySon = subCategorySonId;
        }
      }

      this.#setupProductAnchorLinks(product);

      return product as ProductResume;

    }) as ProductResume[];
  });

  marketStatus = computed(() => this.signalsStore.marketStatus());
  closedMarket = computed(() => {
    const ms = this.marketStatus();
    return {
      title: ms.bannerTitle || `Our Online Market Opens ${ms?.openingDay?.toUpperCase() || ''} Morning!`,
      legend: ms.bannerContent || `We're busy sourcing next week's availability! Check back ${ms?.openingDay || ''} at ${ms?.openingHour || ''} am to begin shopping.`
    };
  })

  isContentLoading = computed(() => this.#productsService.isLoading());
  isContentLoaded = computed(() => this.#productsService.isLoaded());

  isMobile = computed(() => this.#resolutionService.isMobile());

  emptyMessage = 'No products were found.';
  hasGlobalMessages = computed(() => !!this.signalsStore.globalMessages().length);

  subscribedBundles = computed(() => this.#orderService.getBundlesLikeSubscriptions());
  globalMessagesDisplayed = computed(() => this.signalsStore.globalMessages());

  @ViewChild('layoutShopMain') layoutShopMain!: ElementRef;

  // Performance optimization: throttle scroll handling
  #scrollFrameId: number | null = null;
  #pendingScrollEvent: any = null;
  #cachedElementPositions: Map<HTMLElement, { rect: DOMRect, timestamp: number }> = new Map();
  #positionCacheTimeout = 150; // Cache positions for 150ms
  #categoryBannerCache = new Map<string, { banner: string, url: string, show: boolean }>();
  #lastScrollTime = 0;
  #scrollThrottleMs = 150; // Throttle scroll processing to every 150ms for category tracking
  #anchorLinkCache = new Map<string, string>(); // Cache for formatted anchor links

  numberBundleSubscribed = computed(() => this.signalsStore.numberBundleSubscribed());

  productDetails = signal<Map<number, Product>>(new Map()); // Cache of product details
  #visibleProducts$ = new Subject<number[]>(); // IDs of products that are visible in the viewport

  filters = computed(() => this.signalsStore.getFilters());

  isInSearchMode = this.signalsStore.isInSearchMode;
  searchResultCount = this.signalsStore.searchResultCount;

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    afterNextRender(() => {
      // Use requestAnimationFrame to batch DOM reads and avoid forced reflows
      requestAnimationFrame(() => {
        const isMobile = window.innerWidth <= MEDIUM;
        if (isMobile) this.signalsStore.isSidebarOpen.set(false);
      });
    });

    // Clear banner cache when resolution changes (desktop <-> mobile)
    let previousIsMobile: boolean | null = null;
    effect(() => {
      const currentIsMobile = this.isMobile();
      // Only clear cache when value actually changes (not on first run)
      if (previousIsMobile !== null && previousIsMobile !== currentIsMobile) {
        this.#categoryBannerCache.clear();
      }
      previousIsMobile = currentIsMobile;
    });

    // Clear banner cache when menu data changes (fixes OnPush issue)
    effect(() => {
      const menu = this.signalsStore.menuSignal();
      // When menu data loads, clear cache so banners are fetched with fresh data
      if (menu && menu.length > 0) {
        this.#categoryBannerCache.clear();
      }
    });

    // React to search mode query changes
    toObservable(this.signalsStore.searchModeQuery)
      .pipe(skip(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe(query => {
        if (query) this.#enterSearchMode(query);
      });
  }

  ngOnInit(): void {
    this.#setUpSEOTitle();
    this.signalsStore.setIsInShopComponent(true);

    // Browser back from /shop?q=… to /shop drops the q param. Detect that and exit
    // search mode so the user lands back on the normal shop view (which also fires the
    // scroll-position restore wired into exitSearchMode).
    this.#activatedRoute.queryParamMap
      .pipe(skip(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe(params => {
        const q = params.get('q');
        if (!q && this.signalsStore.isInSearchMode()) {
          this.exitSearchMode();
        }
      });

    const searchQuery = this.#activatedRoute.snapshot.queryParamMap.get('q');
    if (searchQuery) {
      // If the signal already matches (came from another page's navigateToSearch), the .set()
      // below is a no-op and the searchModeQuery subscription's skip(1) swallows the only
      // emission — so #enterSearchMode would never fire. Call it explicitly in that case.
      const alreadyMatches = this.signalsStore.searchModeQuery() === searchQuery;
      this.signalsStore.isInSearchMode.set(true);
      this.signalsStore.searchModeQuery.set(searchQuery);
      if (alreadyMatches) this.#enterSearchMode(searchQuery);
    }
    else if (this.signalsStore.filterByProducer()) {
      setTimeout(() => {
        this.#headerService.displaySearchBar.set(true);
      }, 500);
    }
    else
      this.#getProductsResolverData();

    this.#setupBatchRequests();

    // Invalidate position cache when products change
    this.#productsService.productsSignal().length; // Trigger signal read
    // Note: Cache will be invalidated naturally by timestamp expiration
  }

  ngAfterViewInit(): void {
    // Defer scroll operations to avoid forced reflows during initialization
    // Use double requestAnimationFrame to ensure layout is complete
    if (!isPlatformBrowser(this.platformId)) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.scrollIntoView();
      });
    });
  }

  #getProductsResolverData() {
    if (!isPlatformBrowser(this.platformId)) return;
    // Check if resolver data is available (from SSR or previous navigation)
    const resolverData = this.#activatedRoute.snapshot.data['data'];

    if (resolverData) {
      // Resolver data exists - use it instead of making API call
      // This prevents duplicate calls during hydration
      resolverData.subscribe((response: any) => {
        if (response?.data) {
          // Process the resolver data the same way getAllProductsResume does
          this.#productsService.setUpProductsResponse(
            response.data
          );
          // Set loading states
          this.#productsService.isLoading.set(false);
          this.#productsService.isLoaded.set(true);
          // this.scrollIntoView();
        }
      });
    } else {
      // No resolver data - make API call (for routes without resolver)
      this.#productsService.getAllProductsResume(true).pipe(
        // tap(() => this.scrollIntoView())
      ).subscribe();
    }
  }

  scrollIntoView() {
    if (!isPlatformBrowser(this.platformId)) return;
    const selectedProductId = localStorage.getItem('selectedProductId');
    if (selectedProductId != null) {
      this.#goToSelectedProduct(selectedProductId);
    } else {
      this.#handleRouteChanges();
    }
  }

  #goToSelectedProduct(selectedProductId: string) {
    // Use requestAnimationFrame to batch DOM reads and avoid forced reflows
    requestAnimationFrame(() => {
      const scrollToTop = document.getElementById(selectedProductId!.toString());
      if (scrollToTop) {
        const parent = document.getElementById('layoutShopMain');
        if (parent) {
          // Batch the offsetTop read with the scroll operation
          const targetTop = scrollToTop.offsetTop - 100;
          parent.scrollTo({
            top: targetTop,
            left: 0,
            behavior: 'instant'
          });
        }
      }
      localStorage.removeItem('selectedProductId');
    });
  }

  toggleSidebar() {
    this.signalsStore.isSidebarOpen.update((currentValue) => !currentValue);
  }

  ngOnDestroy(): void {
    // Cancel any pending scroll frame
    if (this.#scrollFrameId !== null) {
      cancelAnimationFrame(this.#scrollFrameId);
      this.#scrollFrameId = null;
    }
    // Clear caches
    this.#cachedElementPositions.clear();
    this.#categoryBannerCache.clear();
    this.#anchorLinkCache.clear();
    this.#headerService.clearFilters(true);
    this.signalsStore.isInSearchMode.set(false);
    this.signalsStore.searchModeQuery.set('');
    this.signalsStore.setIsInShopComponent(false);
  }

  trackByFilterKey(_index: number, entry: [string, any]): string {
    return entry[0];
  }

  onScroll(event: any) {
    // Run scroll handling outside Angular zone to prevent change detection
    this.#ngZone.runOutsideAngular(() => {
      // Store the event for processing in requestAnimationFrame
      this.#pendingScrollEvent = event;

      // Use requestAnimationFrame to batch DOM reads and avoid forced reflows
      if (this.#scrollFrameId === null) {
        this.#scrollFrameId = requestAnimationFrame(() => {
          this.#processScroll(this.#pendingScrollEvent);
          this.#scrollFrameId = null;
          this.#pendingScrollEvent = null;
        });
      }
    });
  }

  #processScroll(event: any) {
    if (!event) return;

    // Batch all DOM reads together at the start
    const scrollElement = event.srcElement;
    const scrollTop = scrollElement.scrollTop;
    const scrollHeight = scrollElement.scrollHeight;
    const clientHeight = scrollElement.clientHeight;
    const isBottom = scrollHeight - scrollTop <= clientHeight + 5;

    // Throttle the heavy category tracking logic
    const currentTime = performance.now();
    if (currentTime - this.#lastScrollTime < this.#scrollThrottleMs) {
      return; // Skip heavy processing if called too recently
    }
    this.#lastScrollTime = currentTime;

    if (!this.subcategories.length)
      return;

    // Batch all getBoundingClientRect calls together
    const layoutMainElement = document.getElementsByClassName('layout-shop__main')[0] as HTMLElement;
    if (!layoutMainElement) return;

    // Use cached position if available and recent, otherwise read fresh
    const now = performance.now();
    let contentY: number;
    const cachedLayout = this.#cachedElementPositions.get(layoutMainElement);
    if (cachedLayout && (now - cachedLayout.timestamp) < this.#positionCacheTimeout) {
      contentY = cachedLayout.rect.y;
    } else {
      const rect = layoutMainElement.getBoundingClientRect();
      contentY = rect.y;
      this.#cachedElementPositions.set(layoutMainElement, { rect, timestamp: now });
    }

    if (!contentY) return;

    // Read all element positions in a single batch
    const subcategoryElements = this.subcategories.toArray();
    const subcategorySonElements = this.subcategoriesSon.toArray();

    // Batch read all bounding rects at once, using cache when possible
    const subcategoryRects = subcategoryElements.map(x => {
      const element = x.nativeElement;
      const cached = this.#cachedElementPositions.get(element);
      let rect: DOMRect;

      if (cached && (now - cached.timestamp) < this.#positionCacheTimeout) {
        rect = cached.rect;
      } else {
        rect = element.getBoundingClientRect();
        this.#cachedElementPositions.set(element, { rect, timestamp: now });
      }

      return {
        element: x,
        rect,
        categoryId: +element.getAttribute('category-id'),
        subCategoryId: +element.getAttribute('sub-category-id'),
        subCategorySonId: +element.getAttribute('sub-category-son-id'),
        anchorLink: element.getAttribute('shop-data-anchor-link')
      };
    });

    const subcategorySonRects = subcategorySonElements.map(x => {
      const element = x.nativeElement;
      const cached = this.#cachedElementPositions.get(element);
      let rect: DOMRect;

      if (cached && (now - cached.timestamp) < this.#positionCacheTimeout) {
        rect = cached.rect;
      } else {
        rect = element.getBoundingClientRect();
        this.#cachedElementPositions.set(element, { rect, timestamp: now });
      }

      return {
        element: x,
        rect,
        categoryId: +element.getAttribute('category-id'),
        subCategoryId: +element.getAttribute('sub-category-id'),
        subCategorySonId: +element.getAttribute('sub-category-son-id'),
        anchorLink: element.getAttribute('shop-data-anchor-link')
      };
    });

    let element: ElementRef<any> | undefined;
    let categoryId: number | null = null;
    let subCategoryId: number | null = null;
    let subCategorySonId: number | null = null;
    let viewportAnchorLink: string | null = null;

    if (isBottom) {
      const lastSubcategory = subcategoryRects[subcategoryRects.length - 1];
      element = lastSubcategory?.element;
      if (element && +(element.nativeElement?.getAttribute('sub-category-son-id') || 0) !== -1) {
        const lastSubcategorySon = subcategorySonRects[subcategorySonRects.length - 1];
        element = lastSubcategorySon?.element;
        if (lastSubcategorySon) {
          categoryId = lastSubcategorySon.categoryId;
          subCategoryId = lastSubcategorySon.subCategoryId;
          subCategorySonId = lastSubcategorySon.subCategorySonId;
          viewportAnchorLink = lastSubcategorySon.anchorLink;
        }
      } else if (lastSubcategory) {
        categoryId = lastSubcategory.categoryId;
        subCategoryId = lastSubcategory.subCategoryId;
        subCategorySonId = lastSubcategory.subCategorySonId;
        viewportAnchorLink = lastSubcategory.anchorLink;
      }
    } else {
      // Use viewport-relative thresholds for better adaptation to screen sizes and banner heights
      const viewportHeight = layoutMainElement.clientHeight || 600;
      const upperThreshold = -(viewportHeight * 0.6);  // Element can be up to 60% of viewport above (handles tall banners)
      const lowerThreshold = viewportHeight * 0.5;    // Element must be in top 50% to trigger

      // Find visible element closest to contentY (considers element height for banners)
      let foundElement: typeof subcategorySonRects[number] | undefined = subcategorySonRects
        .filter(x => {
          const elementTop = x.rect.y - contentY;
          const elementBottom = elementTop + x.rect.height;
          // Element is visible if its bottom is below upper threshold AND top is above lower threshold
          return elementBottom >= upperThreshold && elementTop <= lowerThreshold;
        })
        .sort((a, b) => {
          // Sort by distance from contentY - prefer elements closer to viewport top
          const aTop = a.rect.y - contentY;
          const bTop = b.rect.y - contentY;
          return Math.abs(aTop) - Math.abs(bTop);
        })[0];

      if (!foundElement) {
        foundElement = subcategoryRects
          .filter(x => {
            const elementTop = x.rect.y - contentY;
            const elementBottom = elementTop + x.rect.height;
            return elementBottom >= upperThreshold && elementTop <= lowerThreshold;
          })
          .sort((a, b) => {
            const aTop = a.rect.y - contentY;
            const bTop = b.rect.y - contentY;
            return Math.abs(aTop) - Math.abs(bTop);
          })[0];
      }

      if (!foundElement) {
        // Search backwards for elements above viewport
        const subAbove = [...subcategoryRects].reverse().find(x => x.rect.y - contentY < 0);
        const subsonAbove = [...subcategorySonRects].reverse().find(x => x.rect.y - contentY < 0);

        const absSub = Math.abs((subAbove?.rect.y || 0) - contentY);
        const absSubSon = Math.abs((subsonAbove?.rect.y || 0) - contentY);
        const absSubDiff = Math.abs(absSub - 200);
        const absSubSonDiff = Math.abs(absSubSon - 200);

        foundElement = absSubDiff >= absSubSonDiff ? subsonAbove : subAbove;
      }

      if (foundElement) {
        element = foundElement.element;
        categoryId = foundElement.categoryId;
        subCategoryId = foundElement.subCategoryId;
        subCategorySonId = foundElement.subCategorySonId;
        viewportAnchorLink = foundElement.anchorLink;
      }
    }

    if (!element || categoryId === null) return;

    // Update anchor link if needed
    if (viewportAnchorLink) {
      this.changeAnchorLink(viewportAnchorLink);
    }

    // Check if category changed before updating signal
    const currentViewPort = this.signalsStore.categoryInViewPort();
    if (currentViewPort?.categoryId === categoryId &&
      currentViewPort?.subCategoryId === subCategoryId &&
      currentViewPort?.subCategorySonId === subCategorySonId) {
      return;
    }

    // Run signal update inside Angular zone to trigger change detection
    this.#ngZone.run(() => {
      this.signalsStore.categoryInViewPort.set({
        viewportAnchorLink: viewportAnchorLink ?? undefined,
        categoryId,
        subCategoryId: subCategoryId ?? 0,
        subCategorySonId: subCategorySonId ?? 0
      });
    });
  }

  #getCategoryBanner(categoryId: number, subCategoryId?: number, subCategorySonId?: number) {
    // Create cache key
    const cacheKey = `${categoryId}_${subCategoryId ?? ''}_${subCategorySonId ?? ''}`;

    // Check cache first
    if (this.#categoryBannerCache.has(cacheKey)) {
      return this.#categoryBannerCache.get(cacheKey)!;
    }

    let item = this.signalsStore.menuSignal()
      .find(x => x.id === categoryId);

    if (subCategoryId)
      item = item?.subCategories.find((x: any) => x.id === subCategoryId);

    if (subCategorySonId)
      item = item?.subCategories.find((x: any) => x.id === subCategorySonId);

    const result = this.#validateBanner(item);

    // Store in cache
    this.#categoryBannerCache.set(cacheKey, result);

    return result;
  }

  #validateBanner(item: any): { banner: string, url: string, show: boolean } {
    const banner = item?.banners?.[this.isMobile() ? 'mobile' : 'desktop'];

    if (!banner)
      return { banner: '', url: '', show: true };

    return {
      banner,
      url: item.banners?.url || '',
      show: true
    };
  }

  #checkTagsFilter() {
    return this.#productsService.tagsSignal().some(tag => tag.isChecked());
  }

  #setupBatchRequests() {
    const BATCH_SIZE = 15;
    const MAX_CONCURRENT_REQUESTS = 2;

    const noActivity$ = this.#visibleProducts$.pipe(
      switchMap(() => timer(150).pipe(map(() => true))),
      startWith(false)
    );

    this.#visibleProducts$.pipe(
      bufferWhen(() => noActivity$.pipe(filter(inactive => inactive))),
      filter(productGroups => productGroups.length > 0),
      mergeMap(productGroups => productGroups.flat()),
      scan((acc: number[], productId: number) => [...new Set([...acc, productId])], []),
      auditTime(400),
      switchMap(productIds => {
        const idsToFetch = productIds.filter(id => !this.productDetails().has(id));

        if (idsToFetch.length === 0) return of([]);

        // Divide todos los productos en batches
        const chunks: number[][] = [];
        for (let i = 0; i < idsToFetch.length; i += BATCH_SIZE) {
          chunks.push(idsToFetch.slice(i, i + BATCH_SIZE));
        }

        // Invierte el orden: procesa los últimos productos primero (los que el usuario ve ahora)
        chunks.reverse();

        // Procesa todos los batches con límite de concurrencia
        return from(chunks).pipe(
          mergeMap(
            chunk => this.#productsService.getProductsDetailsByIds(chunk).pipe(
              catchError(() => of({}))
            ),
            MAX_CONCURRENT_REQUESTS
          ),
          toArray()
        );
      })
    ).subscribe(responses => {
      this.productDetails.update(prev => {
        const updatedMap = new Map(prev);

        responses.forEach(details => {
          Object.entries(details).forEach(([index, detail]) => {
            const productDetail: Product = detail as Product;
            const productId = Number(productDetail.id);
            updatedMap.set(productId, productDetail);
          });
        });

        return updatedMap;
      });
    });
  }

  onProductCardReady(productId: number) {
    if (this.productDetails().has(productId)) return;
    else this.#visibleProducts$.next([productId]);
  }

  onProductCardHover(productId: number) {
    if (this.productDetails().has(productId)) return;
    else this.#visibleProducts$.next([productId]);
  }

  removeChipFilter(key: string) {
    if (key && this.filters().has(key)) {
      const type = this.filters().get(key)?.type;
      if (!type) return;

      this.signalsStore.setFilterChipRemoved(key);
    }
  }

  #setupProductAnchorLinks(product: ProductResume) {
    // Skip if anchor links already exist (product was already processed)
    if (product.categoryAnchorLink) return;

    const anchorLinkArray = ['shop'];

    if (product.category.name) {
      // Use cached formatted URL or compute and cache it
      const categoryKey = `cat_${product.category.id}`;
      let formattedCategory = this.#anchorLinkCache.get(categoryKey);
      if (!formattedCategory) {
        formattedCategory = formatStringForURL(product.category.name);
        this.#anchorLinkCache.set(categoryKey, formattedCategory);
      }
      anchorLinkArray.push(formattedCategory);
      product.categoryAnchorLink = `/${anchorLinkArray.join('/')}`;
    }

    if (product.subcategory?.name) {
      const subKey = `sub_${product.subcategory.id}`;
      let formattedSub = this.#anchorLinkCache.get(subKey);
      if (!formattedSub) {
        formattedSub = formatStringForURL(product.subcategory.name);
        this.#anchorLinkCache.set(subKey, formattedSub);
      }
      anchorLinkArray.push(formattedSub);
      product.subcategoryAnchorLink = `/${anchorLinkArray.join('/')}`;
    }

    if (product.subcategorySon?.name) {
      const subSonKey = `subson_${product.subcategorySon.id}`;
      let formattedSubSon = this.#anchorLinkCache.get(subSonKey);
      if (!formattedSubSon) {
        formattedSubSon = formatStringForURL(product.subcategorySon.name);
        this.#anchorLinkCache.set(subSonKey, formattedSubSon);
      }
      anchorLinkArray.push(formattedSubSon);
      product.subcategorySonAnchorLink = `/${anchorLinkArray.join('/')}`;
    }
  }

  #handleRouteChanges() {
    // Defer DOM queries to avoid forced reflows
    requestAnimationFrame(() => {
      if (this.signalsStore.categoryInViewPort()?.viewportAnchorLink) {
        const element: any = document.querySelector(`[shop-data-anchor-link="${this.signalsStore.categoryInViewPort()?.viewportAnchorLink ?? '/shop'}"]`);
        if (element) {
          this.#scrollToTarget(element);
          return;
        }
      }

      const category = this.#activatedRoute.snapshot.paramMap.get('category');
      const subcategory = this.#activatedRoute.snapshot.paramMap.get('subcategory');
      const subcategorySon = this.#activatedRoute.snapshot.paramMap.get('slug');
      const routeArray = [];
      if (category) routeArray.push(category);
      if (subcategory) routeArray.push(subcategory);
      if (subcategorySon) routeArray.push(subcategorySon);

      const anchorLink = `/shop/${routeArray.join('/')}`;

      const element: any = document.querySelector(`[shop-data-anchor-link="${anchorLink}"]`);
      if (!element) {
        return;
      }

      this.#scrollToTarget(element);
    });
  }

  #scrollToTarget(element: any) {
    requestAnimationFrame(() => {
      const parent = document.getElementById('layoutShopMain');
      if (parent) {
        // Batch the offsetTop read with the scroll operation
        const targetTop = element.offsetTop;
        parent.scrollTo({
          top: targetTop,
          left: 0,
          behavior: 'instant'
        });
      }
    });
  }

  async #enterSearchMode(query: string): Promise<void> {
    if (!query) return;
    // Empty the grid first so the user sees a clean loading state rather than the previous
    // category's products briefly anchored under the new scrollTop.
    this.#productsService.clearProductsAndStock();
    this.#productsService.isLoading.set(true);
    this.#productsService.isLoaded.set(false);
    try {
      // No explicit sort_by — Typesense defaults to `_text_match:desc`, which is what makes
      // `prioritize_token_position: true` take effect. Adding `,position:asc` as a tiebreaker
      // surfaces an empty-results error; client-side rerankByNameMatch handles fine-grained
      // ranking after the response anyway.
      const result = await this.#typesenseService.search(query, { perPage: 250 });
      this.searchResultCount.set(result.totalFound);
      this.#productsService.setProductsFromSearch(result.products);
    } finally {
      this.#productsService.isLoading.set(false);
      this.#productsService.isLoaded.set(true);
      this.layoutShopMain?.nativeElement?.scrollTo({ top: 0, behavior: 'instant' });
    }
  }

  exitSearchMode(): void {
    // Do NOT pre-clear isInSearchMode / searchModeQuery here. HeaderService.clearFilters
    // reads isInSearchMode as `wasInSearchMode` to decide whether to refetch the catalog.
    // Pre-clearing short-circuits that refetch and leaves stale search results on screen.
    this.searchResultCount.set(0);
    this.#headerService.searchBarContent.set('');
    this.#headerService.clearFilters(true);

    // Drop the ?q= query param so a refresh doesn't re-enter search mode.
    if (this.#activatedRoute.snapshot.queryParamMap.has('q')) {
      this.#router.navigate([], {
        relativeTo: this.#activatedRoute,
        queryParams: { q: null },
        queryParamsHandling: 'merge',
      });
    }

    this.signalsStore.restorePreSearchScroll(getCurrentPathFromUrl(this.#router.url));
  }

  changeAnchorLink(anchorLink: string) {
    this.#location.replaceState(anchorLink);
  }

  #setUpSEOTitle() {
    const { shopTitle, titleSuffix } = environment.config.seo;
    const categorySlug = this.#activatedRoute.snapshot.paramMap.get('category');
    const subcategorySlug = this.#activatedRoute.snapshot.paramMap.get('subcategory');

    if (!categorySlug) {
      this.#canonicalService.setPageTitle(shopTitle);
      this.#canonicalService.setPageDescription('Shop 400+ fresh, local products from NC farmers and artisans. Produce, meat, dairy, bakery & more delivered to your door.');
      this.#canonicalService.setPageType('website');
      return;
    }

    const displayName = this.#resolveCategoryDisplayName(categorySlug, subcategorySlug);
    if (displayName) {
      this.#canonicalService.setPageTitle(`${displayName} ${titleSuffix}`);
    } else {
      const fallbackName = capitalizeWords((subcategorySlug || categorySlug).replace(/-/g, ' '));
      this.#canonicalService.setPageTitle(`${fallbackName} ${titleSuffix}`);
    }

    const descriptionName = displayName || capitalizeWords((subcategorySlug || categorySlug).replace(/-/g, ' '));
    this.#canonicalService.setPageDescription(`Shop ${descriptionName} — fresh local food delivered to Raleigh, Durham & the Triangle by The Produce Box.`);
    this.#canonicalService.setPageType('website');
  }

  #resolveCategoryDisplayName(categorySlug: string, subcategorySlug: string | null): string | null {
    const menu = this.signalsStore.menuSignal();
    if (!menu?.length) return null;

    const category = menu.find((item: any) => formatStringForURL(item.name) === categorySlug);
    if (!category) return null;

    if (!subcategorySlug) return category.name;

    const subcategory = category.subCategories?.find((sub: any) => formatStringForURL(sub.name) === subcategorySlug);
    return subcategory?.name || null;
  }

}
